import ReactQuill, { Quill } from "react-quill";
import Emoji from "quill-emoji";
import { useRef, useState, useMemo, useEffect, useCallback } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import { withSnackbar } from "../../utils/SnackbarHOC";
import axiosInstance from "../../utils/API";
import { extractFilename, formatFileSize, isImageFilename } from "../../utils/EditorUtil";

import "quill-paste-smart";
import "react-quill/dist/quill.snow.css";
import "quill-emoji/dist/quill-emoji.css";
import "./style.css";

Quill.register("modules/emoji", Emoji);

const icons = Quill.import("ui/icons");
icons.attachment = `
<svg viewBox="0 0 18 18">
  <path
    d="M5.5 9.5l5.7-5.7a3 3 0 1 1 4.2 4.2l-7.1 7.1a4.5 4.5 0 0 1-6.4-6.4l7.1-7.1"
    stroke="currentColor"
    stroke-width="1.5"
    fill="none"
    stroke-linecap="round"
  />
</svg>
`;


function EditorCustom({
  value,
  handleSubmit = () => {},
  setValue,
  snackbarShowMessage,
  enableOnBlur,
  onAttachmentsChange,
  initialAttachments,
  ...rest
}) {
  const [readOnly, setReadOnly] = useState(false);
  const [isPasting, setIsPasting] = useState(false);
  const [text, setText] = useState(value);
  const [attachments, setAttachments] = useState(initialAttachments ?? []);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  const quillRef = useRef(null);
  const fileInputRef = useRef(null);
  const createdBlobUrlsRef = useRef([]);
  const linkRangeRef = useRef(null);

  const handleBlur = () => {
    if (!isPasting) {
      setReadOnly(true);
      handleSubmit(text);
    }
  };

  const handleKey = (e, state) => {
    if (e.key === "Control" || e.key === "Meta") setIsPasting(state);
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [["bold", "italic", "underline", "link", "emoji", "attachment"]],
        handlers: {
          attachment: () => fileInputRef.current?.click(),
          link: () => {
            const quill = quillRef.current?.getEditor();
            const selection = quill?.getSelection();
            linkRangeRef.current = selection;
            if (selection && selection.length > 0) {
              const format = quill.getFormat(selection.index, selection.length);
              setLinkUrl(typeof format.link === "string" ? format.link : "");
            } else {
              setLinkUrl("");
            }
            setLinkDialogOpen(true);
          },
        },
      },
      clipboard: { matchVisual: false },
      "emoji-toolbar": true,
      "emoji-shortname": true,
    }),
    []
  );

  const applyLink = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    const range = linkRangeRef.current;
    const url = linkUrl.trim();
    if (quill && range) {
      if (!url) {
        quill.formatText(range.index, range.length, "link", false);
      } else if (range.length > 0) {
        quill.formatText(range.index, range.length, "link", url);
      } else {
        quill.insertText(range.index, url, { link: url });
        quill.setSelection(range.index + url.length);
      }
    }
    setLinkDialogOpen(false);
    setLinkUrl("");
  }, [linkUrl]);

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("categorie", "emailtype");
    const { data } = await axiosInstance.post(
      "/candidatsmanagement/uploaded-files/message-types/add",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return data;
  };

  const secureOpenById = async (id, filenameFallback) => {
    const res = await axiosInstance.get(`/candidatsmanagement/downloadfile/MessageTypes/${id}`, {
      responseType: "blob",
    });
    const contentType = res.headers?.["content-type"] || "";
    const dispo = res.headers?.["content-disposition"] || "";
    const filename = extractFilename(dispo) || filenameFallback || "download";
    const blob = new Blob([res.data], { type: contentType });
    const blobUrl = URL.createObjectURL(blob);
    const canPreview =
      contentType.startsWith("image/") || contentType === "application/pdf";
    if (canPreview) {
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      return;
    }
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  };

  const removeAttachment = useCallback((id) => {
    setAttachments((prev) => {
      const item = prev.find((a) => a.id === id);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      snackbarShowMessage("Upload en cours...", "warning", null);
      const data = await uploadFile(file);
      const id = data?.id;
      if (!id) throw new Error("No id returned by server");
      let previewUrl = null;
      if (file.type?.startsWith("image/")) {
        previewUrl = URL.createObjectURL(file);
        createdBlobUrlsRef.current.push(previewUrl);
      }
      setAttachments((prev) => [
        ...prev,
        { id, name: file.name, type: file.type, size: file.size, previewUrl },
      ]);
      snackbarShowMessage(
        file.type?.startsWith("image/") ? "Image ajoutée" : "Pièce jointe ajoutée ✅",
        "success",
        2500
      );
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Erreur upload";
      snackbarShowMessage(`Erreur: ${msg}`, "error", 4000);
    }
  };

  useEffect(() => {
    if (enableOnBlur) setText(value);
  }, [value]);

  // Hydrate les thumbnails des images venant de initialAttachments (edit mode)
  useEffect(() => {
    if (!initialAttachments?.length) return;
    const toHydrate = initialAttachments.filter(
      (a) => !a.name || (isImageFilename(a.name) && !a.previewUrl)
    );
    if (!toHydrate.length) return;

    let cancelled = false;
    (async () => {
      const updates = await Promise.all(
        toHydrate.map(async (att) => {
          try {
            const res = await axiosInstance.get(
              `/candidatsmanagement/downloadfile/MessageTypes/${att.id}?inline=1`,
              { responseType: "blob" }
            );
            const contentType = res.headers?.["content-type"] || "";
            const dispo = res.headers?.["content-disposition"] || "";
            const name = att.name || extractFilename(dispo) || att.id;
            const size = att.size || res.data.size || 0;
            let previewUrl = null;
            if (isImageFilename(name) || contentType.startsWith("image/")) {
              previewUrl = URL.createObjectURL(res.data);
              createdBlobUrlsRef.current.push(previewUrl);
            }
            return { id: att.id, name, size, previewUrl };
          } catch {
            return null;
          }
        })
      );
      if (cancelled) return;
      const metaMap = Object.fromEntries(
        updates.filter(Boolean).map((u) => [u.id, u])
      );
      setAttachments((prev) =>
        prev.map((a) => (metaMap[a.id] ? { ...a, ...metaMap[a.id] } : a))
      );
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onAttachmentsChange?.(attachments.map(({ id, name, type, size }) => ({ id, name, type, size })));
  }, [attachments]);

  useEffect(() => {
    return () => {
      createdBlobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      createdBlobUrlsRef.current = [];
    };
  }, []);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={onFilePicked}
      />
      <div className="editor-custom-wrap">
        <ReactQuill
          ref={quillRef}
          modules={modules}
          theme="snow"
          readOnly={readOnly}
          value={enableOnBlur ? text : value}
          onBlur={enableOnBlur ? handleBlur : () => {}}
          onKeyDown={(e) => handleKey(e, true)}
          onKeyUp={(e) => handleKey(e, false)}
          onChange={(html) => {
            if (enableOnBlur) setText(html);
            else setValue(html);
          }}
          placeholder="Saisie le text ici..."
          {...rest}
        />
        {attachments.length > 0 && (
          <div className="attachments-zone">
            {attachments.map((att) => (
              <div key={att.id} className="attachment-chip">
                <span className="attachment-icon">📎</span>
                <span
                  className="attachment-name"
                  onClick={() => secureOpenById(att.id, att.name)}
                  title={att.name}
                >
                  {att.name || att.id}
                </span>
                {att.size ? (
                  <span className="attachment-size">{formatFileSize(att.size)}</span>
                ) : null}
                <button
                  className="attachment-remove"
                  onClick={() => removeAttachment(att.id)}
                  title="Supprimer"
                  type="button"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Insérer un lien</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="URL"
            placeholder="https://..."
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyLink()}
            variant="outlined"
            size="small"
            style={{ marginTop: 8 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Annuler</Button>
          <Button onClick={applyLink} color="primary" variant="contained">
            Insérer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default withSnackbar(EditorCustom);

